import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { Router } from "@angular/router";
import {
  ApexAxisChartSeries,
  ApexOptions,
  NgApexchartsModule,
} from "ng-apexcharts";
import { chartColorsForBookingStatuses } from "../../shared/charts/chart-tokens";
import { formatBookingStatusLabel } from "../../shared/booking-status-label";
import { formatAedCurrency } from "../../shared/aed-currency";
import { useVenueWallClockForDisplay } from "../../shared/venue-wall-clock";
import { formatProductDate } from "../../shared/product-date";
import { AccountService } from "../admin/settings/account/account.service";
import { BookingService } from "../bookings/booking.service";
import { BOOKING_DIALOG_CONFIG } from "../bookings/booking/booking-dialog.config";
import { BookingComponent } from "../bookings/booking/booking.component";
import { CourtOccupancyChartComponent } from "./court-occupancy-chart.component";
import {
  buildBookingsTrendChartOptions,
  buildBookingStatusChartOptions,
} from "./overview-charts";

type LoadState = "loading" | "ready" | "error";

interface ScheduleItem {
  id?: string;
  _id?: string;
  title?: string;
  location?: string;
  status?: string;
  start: string | Date;
  end: string | Date;
}

interface BookingStatusRow {
  label: string;
  count: number;
  percentage: number;
  color: string;
}

interface SummaryCard {
  label: string;
  value: string;
  supportingText: string;
  icon: string;
  tone: "primary" | "info" | "danger" | "success";
}

@Component({
  selector: "app-overview",
  standalone: true,
  templateUrl: "./overview.component.html",
  styleUrls: ["./overview.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    NgApexchartsModule,
    CourtOccupancyChartComponent,
  ],
})
export class OverviewComponent implements OnInit {
  readonly formatProductDate = formatProductDate;
  @ViewChild(CourtOccupancyChartComponent)
  private occupancyChart?: CourtOccupancyChartComponent;

  readonly todayLabel = formatProductDate(new Date());

  readonly bookingsChartOptions: ApexOptions = buildBookingsTrendChartOptions();

  readonly bookingStatusChartOptions: ApexOptions =
    buildBookingStatusChartOptions();

  userDetails: Record<string, unknown> = {};
  companyDetails: Record<string, unknown> = {};
  schedule: {
    today: ScheduleItem[];
    tomorrow: ScheduleItem[];
    week?: ScheduleItem[];
  } = {
    today: [],
    tomorrow: [],
  };

  bookingsSeries: ApexAxisChartSeries = [];
  bookingStatusSeries: number[] = [];
  bookingStatusLabels: string[] = [];
  bookingStatusTotal = 0;

  statsState: LoadState = "loading";
  trendState: LoadState = "loading";
  statusState: LoadState = "loading";
  scheduleState: LoadState = "loading";

  totalBookings = 0;
  totalConfirmedTentative = 0;
  totalCancelled = 0;
  totalRevenue = 0;
  totalRevenueRefunded = 0;
  totalMaintenance = 0;

  constructor(
    private readonly router: Router,
    public readonly bookingService: BookingService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly accountService: AccountService,
  ) {}

  ngOnInit(): void {
    this.userDetails = this.readStoredUser();
    this.loadCompany();
    this.refreshDashboard();
  }

  get displayName(): string {
    return String(this.userDetails["name"] || "User");
  }

  get companyName(): string {
    return String(this.companyDetails["name"] || "Your company");
  }

  get companyLogo(): string | null {
    const logo = this.companyDetails["logo"];
    return typeof logo === "string" && logo ? logo : null;
  }

  get companyInitial(): string {
    return (this.companyName || this.displayName || "B")
      .slice(0, 1)
      .toUpperCase();
  }

  get isDashboardLoading(): boolean {
    return [
      this.statsState,
      this.trendState,
      this.statusState,
      this.scheduleState,
    ].some((state) => state === "loading");
  }

  get summaryCards(): SummaryCard[] {
    return [
      {
        label: "Total bookings",
        value: this.formatNumber(this.totalBookings),
        supportingText: `${this.formatNumber(this.totalMaintenance)} maintenance blocks recorded`,
        icon: "heroicons_outline:calendar",
        tone: "primary",
      },
      {
        label: "Active bookings",
        value: this.formatNumber(this.totalConfirmedTentative),
        supportingText: "Confirmed and tentative",
        icon: "heroicons_outline:check-circle",
        tone: "info",
      },
      {
        label: "Cancelled bookings",
        value: this.formatNumber(this.totalCancelled),
        supportingText: "All-time total",
        icon: "heroicons_outline:x-circle",
        tone: "danger",
      },
      {
        label: "Revenue",
        value: formatAedCurrency(this.totalRevenue),
        supportingText: `${formatAedCurrency(this.totalRevenueRefunded)} refunded`,
        icon: "heroicons_outline:banknotes",
        tone: "success",
      },
    ];
  }

  get hasBookingTrendData(): boolean {
    return this.bookingsSeries.some((series) =>
      series.data.some((value) => Number(value) > 0),
    );
  }

  get bookingStatusRows(): BookingStatusRow[] {
    return this.bookingStatusLabels.map((label, index) => {
      const percentage = Number(this.bookingStatusSeries[index] ?? 0);
      return {
        label: this.formatStatusLabel(label),
        percentage,
        count: Math.round((this.bookingStatusTotal * percentage) / 100),
        color: chartColorsForBookingStatuses(this.bookingStatusLabels)[index],
      };
    });
  }

  get upcomingBookings(): ScheduleItem[] {
    return this.schedule.week ?? this.schedule.today.slice(0, 5);
  }

  refreshDashboard(): void {
    this.loadStats();
    this.loadBarChart();
    this.loadPieChart();
    this.loadSchedule();
    this.occupancyChart?.load();
  }

  loadCompany(): void {
    this.accountService.getCompany().subscribe({
      next: (response) => {
        this.companyDetails = response?.data || response || {};
        this.cdr.markForCheck();
      },
      error: () => {
        this.companyDetails = {};
        this.cdr.markForCheck();
      },
    });
  }

  loadStats(): void {
    this.statsState = "loading";
    this.bookingService.getBookingStats().subscribe({
      next: (response) => {
        this.totalBookings = Number(response?.totalBookings ?? 0);
        this.totalConfirmedTentative = Number(
          response?.totalConfirmedTentative ?? 0,
        );
        this.totalCancelled = Number(response?.totalCancelled ?? 0);
        this.totalRevenue = Number(response?.totalRevenue ?? 0);
        this.totalRevenueRefunded = Number(response?.totalRevenueRefunded ?? 0);
        this.totalMaintenance = Number(response?.totalMaintenance ?? 0);
        this.statsState = "ready";
        this.cdr.markForCheck();
      },
      error: () => {
        this.statsState = "error";
        this.cdr.markForCheck();
      },
    });
  }

  loadBarChart(): void {
    this.trendState = "loading";
    this.bookingService.getBookingOverTime().subscribe({
      next: (response) => {
        const labels = Array.isArray(response?.labels) ? response.labels : [];
        const rawSeries: ApexAxisChartSeries = Array.isArray(
          response?.series?.["this-week"],
        )
          ? response.series["this-week"]
          : [];
        const series: ApexAxisChartSeries = rawSeries.map((item) => ({
          ...item,
          name: formatBookingStatusLabel(item?.name),
        }));
        const strokeWidths = series.map((item) =>
          String(item?.type).toLowerCase() === "line" ? 3 : 0,
        );
        this.bookingsSeries = series;
        this.bookingsChartOptions.colors = chartColorsForBookingStatuses(
          rawSeries.map((item) => item.name ?? ""),
        );
        this.bookingsChartOptions.xaxis = {
          ...this.bookingsChartOptions.xaxis,
          categories: labels,
        };
        this.bookingsChartOptions.stroke = {
          curve: "smooth",
          width: strokeWidths,
        };
        this.trendState = "ready";
        this.cdr.markForCheck();
      },
      error: () => {
        this.bookingsSeries = [];
        this.trendState = "error";
        this.cdr.markForCheck();
      },
    });
  }

  loadPieChart(): void {
    this.statusState = "loading";
    this.bookingService.getBookingPieChart().subscribe({
      next: (response) => {
        this.bookingStatusLabels = Array.isArray(response?.labels)
          ? response.labels
          : [];
        this.bookingStatusSeries = Array.isArray(response?.series)
          ? response.series
          : [];
        this.bookingStatusTotal = Number(response?.uniqueVisitors ?? 0);
        this.bookingStatusChartOptions.labels = this.bookingStatusLabels.map(
          formatBookingStatusLabel,
        );
        this.bookingStatusChartOptions.colors = chartColorsForBookingStatuses(
          this.bookingStatusLabels,
        );
        this.statusState = "ready";
        this.cdr.markForCheck();
      },
      error: () => {
        this.bookingStatusLabels = [];
        this.bookingStatusSeries = [];
        this.bookingStatusTotal = 0;
        this.statusState = "error";
        this.cdr.markForCheck();
      },
    });
  }

  loadSchedule(): void {
    this.scheduleState = "loading";
    this.bookingService.getBookingSchedule().subscribe({
      next: (response) => {
        this.schedule = {
          week: Array.isArray(response?.schedule?.week)
            ? response.schedule.week.map(useVenueWallClockForDisplay)
            : undefined,
          today: Array.isArray(response?.schedule?.today)
            ? response.schedule.today.map(useVenueWallClockForDisplay)
            : [],
          tomorrow: Array.isArray(response?.schedule?.tomorrow)
            ? response.schedule.tomorrow.map(useVenueWallClockForDisplay)
            : [],
        };
        this.scheduleState = "ready";
        this.cdr.markForCheck();
      },
      error: () => {
        this.schedule = { today: [], tomorrow: [] };
        this.scheduleState = "error";
        this.cdr.markForCheck();
      },
    });
  }

  onCompanyLogoError(): void {
    this.companyDetails = { ...this.companyDetails, logo: null };
    this.cdr.markForCheck();
  }

  onBookingClick(booking: ScheduleItem): void {
    const dialogRef = this.dialog.open(BookingComponent, {
      ...BOOKING_DIALOG_CONFIG,
      data: {
        booking,
        bookingId: booking.id || booking._id,
        isEditMode: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadSchedule();
        this.loadStats();
      }
    });
  }

  viewAllBookings(): void {
    void this.router.navigate(["/bookings"]);
  }

  trackByBooking(index: number, item: ScheduleItem): string | number {
    return item.id || item._id || index;
  }

  bookingStatusClass(status: string | undefined): string {
    const normalized = String(status || "unknown").toLowerCase();
    return `booking-status booking-status--${normalized.replace(/[^a-z0-9]+/g, "-")}`;
  }

  formatStatusLabel(value: string | undefined): string {
    return formatBookingStatusLabel(value);
  }

  private formatNumber(value: number, maximumFractionDigits = 0): string {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits }).format(
      value || 0,
    );
  }

  private readStoredUser(): Record<string, unknown> {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }
}
